import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE || "/api";

class ApiClient {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      withCredentials: true,
    });

    this.client.interceptors.request.use((config) => {
      const method = (config.method || "get").toLowerCase();
      if (["post", "put", "patch", "delete"].includes(method)) {
        const csrfToken = this.getCookie("__Host-portfolio-csrf") || this.getCookie("portfolio_csrf");
        if (csrfToken) config.headers["X-CSRF-Token"] = csrfToken;
      }
      return config;
    });
  }

  getCookie(name) {
    const prefix = `${encodeURIComponent(name)}=`;
    const match = document.cookie
      .split("; ")
      .find((cookie) => cookie.startsWith(prefix));
    return match ? decodeURIComponent(match.slice(prefix.length)) : "";
  }

  setAccessToken(token) {
    if (token) {
      this.client.defaults.headers.common.Authorization = `Bearer ${token}`;
      return;
    }
    delete this.client.defaults.headers.common.Authorization;
  }

  getPortfolioUpdatesUrl() {
    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    return `${protocol}//${window.location.host}/ws/portfolio`;
  }

  async get(path, config = {}) {
    const response = await this.client.get(path, config);
    return response.data;
  }

  async post(path, body, config = {}) {
    const response = await this.client.post(path, body, config);
    return response.data;
  }

  async put(path, body, config = {}) {
    const response = await this.client.put(path, body, config);
    return response.data;
  }

  async patch(path, body, config = {}) {
    const response = await this.client.patch(path, body, config);
    return response.data;
  }

  async delete(path, config = {}) {
    const response = await this.client.delete(path, config);
    return response.data;
  }

  getPortfolioDocument(key) {
    return this.get(`/portfolio/${key}`);
  }

  createPortfolioDocument(key, content) {
    return this.post("/portfolio", { key, content });
  }

  replacePortfolioDocument(key, content) {
    return this.put(`/portfolio/${key}`, { content });
  }

  patchPortfolioDocument(key, content) {
    return this.patch(`/portfolio/${key}`, { content });
  }

  deletePortfolioDocument(key) {
    return this.delete(`/portfolio/${key}`);
  }

  exportPortfolioPages() {
    return this.get("/portfolio/bundle");
  }

  importPortfolioPages(pages) {
    return this.put("/portfolio/bundle", pages);
  }

  getHome() {
    return this.getPortfolioDocument("home");
  }

  updateHome(content) {
    return this.replacePortfolioDocument("home", content);
  }

  getResume() {
    return this.getPortfolioDocument("resume");
  }

  updateResume(content) {
    return this.replacePortfolioDocument("resume", content);
  }

  getProjects() {
    return this.getPortfolioDocument("projects");
  }

  updateProjects(content) {
    return this.replacePortfolioDocument("projects", content);
  }

  uploadPortfolioImage(file) {
    return this.uploadPortfolioMedia(file, "image");
  }

  uploadPortfolioMedia(file, mediaType) {
    const body = new FormData();
    body.append("file", file);
    return this.post(`/portfolio/media/${encodeURIComponent(mediaType)}`, body);
  }

  sendContactMessage(message) {
    return this.post("/contact", message);
  }

  login(username, password) {
    return this.post("/auth/login", { username, password });
  }

  logout() {
    return this.post("/auth/logout");
  }

  getCurrentAdmin() {
    return this.get("/auth/me");
  }

  getPortfolioEditor(key) {
    return this.get(`/portfolio/${key}/editor`);
  }

  getPortfolioSchema(key) {
    return this.get(`/portfolio/${key}/schema`);
  }

  savePortfolioDraft(key, content) {
    return this.put(`/portfolio/${key}/draft`, { content });
  }

  publishPortfolioDraft(key) {
    return this.post(`/portfolio/${key}/publish`);
  }

  getPortfolioRevisions(key) {
    return this.get(`/portfolio/${key}/revisions`);
  }

  restorePortfolioRevision(key, revisionId) {
    return this.post(`/portfolio/${key}/revisions/${revisionId}/restore`);
  }

  getApiTokens() {
    return this.get("/auth/api-tokens");
  }

  getOAuthGrants() {
    return this.get("/auth/oauth-grants");
  }

  pauseOAuthGrant(grantId) {
    return this.post(`/auth/oauth-grants/${encodeURIComponent(grantId)}/pause`);
  }

  resumeOAuthGrant(grantId) {
    return this.post(`/auth/oauth-grants/${encodeURIComponent(grantId)}/resume`);
  }

  revokeOAuthGrant(grantId) {
    return this.delete(`/auth/oauth-grants/${encodeURIComponent(grantId)}`);
  }

  createApiToken(name, scopes, expiresInDays = null) {
    return this.post("/auth/api-tokens", {
      name,
      scopes,
      expires_in_days: expiresInDays,
    });
  }

  revokeApiToken(prefix) {
    return this.delete(`/auth/api-tokens/${prefix}`);
  }
}

const apiClient = new ApiClient();

export default apiClient;
